import React, { PureComponent } from 'react';
import TailwindPatternEditor from 'widgets/bussiness/pattern-editor-tw';
import { ItemReference, QueryResult } from 'stores/dataStore';
import { PatternRowDto } from 'stores/configuration/machinery/pattern-rows-store';
import { WithTranslation, withTranslation } from 'react-i18next';
import { MachineListItemViewModel } from 'site/pages/shared/events-and-requests/machine-list-item';
import TailwindMachineListItem from 'site/pages/shared/events-and-requests/machine-list-item-tw';
import { connect } from 'redux-scaffolding-ts';
import { ClusterDto, DropDownClustersStore } from 'stores/configuration/machinery/clusters-store';
import { EquipmentTypeDto, EquipmentTypesStore } from 'stores/configuration/machinery/equipment-types-store';
import { Dimmer, Loader } from 'semantic-ui-react';
import { HierarchyDto, HierarchyStore, IdNameDto, PatternsStore } from 'stores/configuration/machinery/patterns-store';
import { PatternMachinesViewModel } from './pattern-machines';
import { EquipmentTypeItemReference } from 'widgets/bussiness/equipment-type-editor';
import { customEqualCompareBystringify, isNullOrWhiteSpaces } from 'utils/useful-functions';
import { nameof } from 'utils/object';

export interface TailwindPatternMachinesProps extends WithTranslation {
  readOnly: boolean;
  showRequired?: boolean;
  patternsStore?: PatternsStore;
  hierarchyStore?: HierarchyStore;
  value: PatternMachinesViewModel;
  onChange?: (value: PatternMachinesViewModel) => void;
  className?: string;
  loadPatternsOnOpen?: boolean;
  clusters?: DropDownClustersStore;
  equipmentTypes?: EquipmentTypesStore;
  profession: ItemReference;
  warningMessageEnabled?: boolean;
}

export interface TailwindPatternMachinesState {
  hasPattern: boolean;
  patternRows: PatternRowDto[];
  clusters?: QueryResult<ClusterDto>;
  equipmentTypes?: QueryResult<EquipmentTypeDto>;
  someMachineDeleted: boolean;
  loading: boolean;
}

@connect(
  ['patternsStore', PatternsStore],
  ['hierarchyStore', HierarchyStore],
  ['clusters', DropDownClustersStore],
  ['equipmentTypes', EquipmentTypesStore]
)
class TailwindPatternMachines extends PureComponent<TailwindPatternMachinesProps, TailwindPatternMachinesState> {
  static defaultProps = { onChange: () => {} };
  state: TailwindPatternMachinesState = {
    patternRows: [],
    hasPattern: false,
    someMachineDeleted: false,
    loading: false
  };

  componentDidMount() {
    if (!this.props.value.machines || this.props.value.machines.length === 0) {
      this.props.onChange({
        pattern: null,
        machines: [{ cluster: null, equipmentType: null, oem: null, machineModel: null, machineUnits: [], plcTypes: [] }]
      });
    } else {
      const machinesProp = this.props.value.machines;
      let model: HierarchyDto = {
        clusterIds: machinesProp.flatMap(x => x.cluster?.id).filter(x => x),
        equipmentTypeIds: machinesProp.flatMap(x => x.equipmentType?.id).filter(x => x),
        oemIds: machinesProp.flatMap(x => x.oem?.id).filter(x => x),
        machineModelIds: machinesProp.flatMap(x => x.machineModel?.id).filter(x => x),
        machineUnitIds: machinesProp.flatMap(x => x.machineUnits).filter(x => x),
        plcTypeIds: machinesProp.flatMap(x => x.plcTypes).filter(x => x)
      };

      this.props.hierarchyStore.get(model).then(resp => {
        if (resp.isSuccess) {
          let machines: MachineListItemViewModel[] = [];
          const idNames = resp.item;
          // Get current state to preserve hasAvailablePlcTypes that may have been updated asynchronously
          const currentMachines = this.props.value.machines;
          machines = machinesProp.map((pr, idx) => ({
            cluster: pr.cluster ? this.toItemReference(idNames.clusters.find(x => x.id === pr.cluster.id)) : null,
            equipmentType: pr.equipmentType ? this.toItemReference(idNames.equipmentTypes.find(x => x.id === pr.equipmentType.id)) : null,
            oem: pr.oem ? this.toItemReference(idNames.oems.find(x => x.id === pr.oem.id)) : null,
            machineModel: pr.machineModel ? this.toItemReference(idNames.machineModels.find(x => x.id === pr.machineModel.id)) : null,
            machineUnits: pr.machineUnits,
            plcTypes: pr.plcTypes,
            // Preserve current hasAvailablePlcTypes if it was updated by MultiPlcTypeEditor callback
            hasAvailablePlcTypes: currentMachines[idx]?.hasAvailablePlcTypes ?? pr.hasAvailablePlcTypes,
            machineUnitItems:
              pr.machineUnits && pr.machineUnits.length > 0
                ? idNames.machineUnits.filter(x => pr.machineUnits.includes(x.id)).map(x => this.toItemReference(x))
                : [],
            plcTypeItems:
              pr.plcTypes && pr.plcTypes.length > 0
                ? idNames.plcTypes.filter(x => pr.plcTypes.includes(x.id)).map(x => this.toItemReference(x))
                : []
          }));

          const patternViewModel: PatternMachinesViewModel = { ...this.props.value, machines };
          this.setState({}, () => this.props.onChange(patternViewModel));
        }
      });
    }

    if (this.props.value.pattern && this.props.value.pattern.id) {
      this.setState({ hasPattern: true });
      this.loadPattern(this.props.value.pattern.id).then(pattern => {
        this.setState({ patternRows: pattern?.patternRows });
      });
    }
  }

  private toItemReference = (item: IdNameDto) => {
    if (!item) return null;
    const model: ItemReference = {
      id: item.id,
      title: item.name
    };
    return model;
  };

  private handleOnHasPatternChange = (hasPattern: boolean) => {
    let newValue = { ...this.props.value };
    newValue.pattern = null;
    newValue.machines = !hasPattern
      ? [{ cluster: null, equipmentType: null, oem: null, machineModel: null, machineUnits: [], plcTypes: [] }]
      : [];

    this.setState({ hasPattern: hasPattern });
    this.props.onChange(newValue);
  };

  private handleOnPatternChange = (pattern: ItemReference) => {
    if (!pattern) {
      let value = { ...this.props.value };
      value.pattern = null;
      value.machines = [];

      this.props.onChange(value);
      return;
    }

    this.loadPattern(pattern.id).then(({ patternRows }) => {
      const machines: MachineListItemViewModel[] = this.mapPatternRowsToViewModel(patternRows || []);

      const value: PatternMachinesViewModel = { ...this.props.value, pattern, machines };

      this.setState({ patternRows }, () => this.props.onChange(value));
    });
  };

  private loadPattern = (patternId: string) => this.props.patternsStore.getById(patternId);

  private getClusterMethod() {
    const method = async (search: string) => {
      const filters = isNullOrWhiteSpaces(search) ? [] : [`contains(tolower(${nameof<ClusterDto>('name')}), '${search.toLowerCase()}')`];

      const result = await this.props.clusters.getAllClustersAsync({
        searchQuery: '',
        skip: 0,
        take: 100000,
        orderBy: [{ direction: 'Ascending', field: nameof<ClusterDto>('name'), useProfile: false }],
        filter: filters
      });
      this.setState({ clusters: result });
      return Object.assign({}, result, { items: result.items.map(c => ({ id: c.id, title: c.name })) }) as QueryResult<ItemReference>;
    };
    return method;
  }

  private getEquipmentTypeMethod() {
    const method = async () => {
      const filters: any[] = [{ clusterName: { eq: 'Cigarette Packing' } }];
      filters.push({
        or: [{ name: 'Packer' }, { name: 'Cellophaner / Wrapper' }, { name: 'Tax Stamper' }, { name: 'Cartoner / Parceler' }]
      });

      const result = await this.props.equipmentTypes.getAllAsync({
        searchQuery: '',
        skip: 0,
        take: 100000,
        orderBy: [{ direction: 'Ascending', field: nameof<EquipmentTypeDto>('name'), useProfile: false }],
        filter: filters
      });

      this.setState({ equipmentTypes: result });

      return Object.assign({}, result, {
        items: result.items.map(c => ({ id: c.id, title: c.name, cluster: { id: c.clusterId, title: c.clusterName } }))
      }) as QueryResult<EquipmentTypeItemReference>;
    };
    return method;
  }

  private machineUnitOrPlcTypeHadChanged = (newMachine: MachineListItemViewModel, currentMachine: MachineListItemViewModel): boolean => {
    const newPlc = newMachine?.plcTypes ?? [];
    const curPlc = currentMachine?.plcTypes ?? [];
    const newUnits = newMachine?.machineUnits ?? [];
    const curUnits = currentMachine?.machineUnits ?? [];

    return !customEqualCompareBystringify(newPlc, curPlc) || !customEqualCompareBystringify(newUnits, curUnits);
  };

  canAddAutomaticMachines = (value: MachineListItemViewModel[], currentMachine: MachineListItemViewModel): boolean => {
    let someMachineDeletedCopy = this.state.someMachineDeleted;
    let canAdd: boolean = false;

    const isOnlyOneMachine = value.length === 1;
    const isMachineEmpty = (m: MachineListItemViewModel) => !m.cluster && !m.equipmentType && !m.oem && !m.machineModel;

    const allMachinesEmpty = value.every(isMachineEmpty);
    const isCurrentMachineUnique = value.every(
      x => x.cluster?.id !== currentMachine.cluster?.id || x.equipmentType?.id !== currentMachine.equipmentType?.id
    );

    if (someMachineDeletedCopy && (isOnlyOneMachine || allMachinesEmpty || isCurrentMachineUnique)) {
      someMachineDeletedCopy = false;
      this.setState({ someMachineDeleted: someMachineDeletedCopy });
    }

    canAdd = !someMachineDeletedCopy;
    return canAdd;
  };

  private handleOnMachineChange = async (index: number, newMachine: MachineListItemViewModel) => {
    let value = { ...this.props.value };
    const currentMachine = value.machines[index];

    value.machines[index] = {
      ...currentMachine,
      cluster: newMachine.cluster !== undefined ? newMachine.cluster : currentMachine.cluster,
      equipmentType: newMachine.equipmentType !== undefined ? newMachine.equipmentType : currentMachine.equipmentType,
      oem: newMachine.oem !== undefined ? newMachine.oem : currentMachine.oem,
      machineModel: newMachine.machineModel !== undefined ? newMachine.machineModel : currentMachine.machineModel,
      plcTypes: newMachine.plcTypes !== undefined ? newMachine.plcTypes : currentMachine.plcTypes,
      machineUnits: newMachine.machineUnits !== undefined ? newMachine.machineUnits : currentMachine.machineUnits,
      hasAvailablePlcTypes:
        newMachine.hasAvailablePlcTypes !== undefined ? newMachine.hasAvailablePlcTypes : currentMachine.hasAvailablePlcTypes
    };

    // Check if there were real changes in cluster or equipment type
    const clusterChanged = newMachine.cluster !== undefined && currentMachine.cluster?.id !== newMachine.cluster?.id;
    const equipmentTypeChanged =
      newMachine.equipmentType !== undefined && currentMachine.equipmentType?.id !== newMachine.equipmentType?.id;

    // Only add machines automatically if there were changes in cluster or equipment type
    if (!clusterChanged && !equipmentTypeChanged) {
      this.props.onChange(value);
      return;
    }

    const canAdd = this.canAddAutomaticMachines(value.machines, currentMachine);

    // If automatic machines cannot be added, exit
    if (!canAdd) {
      this.props.onChange(value);
      return;
    }

    this.setState({ loading: true });

    const clustersQuery = await this.getClusterMethod();
    const clusters = await clustersQuery('Cigarette Packing');
    const autoCluster = clusters.items.map(x => x.id);

    if (newMachine != null && newMachine.cluster != null && autoCluster.includes(newMachine.cluster.id)) {
      const equipmentTypesQuery = await this.getEquipmentTypeMethod();
      const equipmentTypes = await equipmentTypesQuery();
      const todasLasOpciones = equipmentTypes.items.map(x => ({ id: x.id, title: x.title } as ItemReference));

      if (newMachine.equipmentType != null && todasLasOpciones.map(x => x.id).includes(newMachine.equipmentType.id)) {
        for (let opcion of todasLasOpciones) {
          const equipmentTypes = value.machines.map(x => x.equipmentType);
          if (
            canAdd &&
            !this.machineUnitOrPlcTypeHadChanged(newMachine, currentMachine) &&
            !equipmentTypes.map(x => x.id).includes(opcion.id) &&
            value.machines.length < 4
          ) {
            value.machines[value.machines.length] = {
              cluster: newMachine.cluster,
              equipmentType: opcion
            } as MachineListItemViewModel;
          }
        }
      }
    }

    this.setState({ loading: false });
    this.props.onChange(value);
  };

  private handleOnDeleteRow = (index: number) => {
    const { value, onChange } = this.props;
    this.setState({ someMachineDeleted: true });
    onChange({ ...value, machines: value.machines.filter((_, i) => i !== index) });
  };

  private mapPatternRowsToViewModel = (patternRows: PatternRowDto[]) => {
    const machines: MachineListItemViewModel[] = (patternRows || []).map(pr => ({
      cluster: pr.machineRelatedClusterId ? { id: pr.machineRelatedClusterId, title: pr.machineRelatedClusterName } : null,
      equipmentType: pr.equipmentTypeId ? { id: pr.equipmentTypeId, title: pr.equipmentTypeName } : null,
      oem: pr.oemId ? { id: pr.oemId, title: pr.oemName } : null,
      machineModel: pr.machineModelId ? { id: pr.machineModelId, title: pr.machineModelName } : null,
      machineUnits: [],
      plcTypes: []
    }));
    return machines;
  };

  private matchMachineClusteAndEquipment = (): boolean => {
    const { machines } = this.props.value;
    const { hasPattern } = this.state;
    let warningEquipmentTypes: string[] = ['Packer', 'Cellophaner / Wrapper', 'Tax Stamper', 'Cartoner / Parceler'];

    if (hasPattern || machines == null) return false;

    let match = machines.any(x => x != null && x.cluster != null && x.cluster.title === 'Cigarette Packing');
    match = match && machines.any(x => x != null && x.equipmentType != null && warningEquipmentTypes.includes(x.equipmentType.title));

    return match;
  };

  private automaticallyAddedMachines = (): boolean => {
    const { machines } = this.props.value;
    return this.matchMachineClusteAndEquipment() && machines.length !== 1;
  };

  canDelete = () => {
    const { readOnly } = this.props;
    const { machines } = this.props.value;
    const { hasPattern } = this.state;
    const showWarningMessage = this.matchMachineClusteAndEquipment();

    return !readOnly && (hasPattern || (showWarningMessage && machines.length > 1));
  };

  render() {
    const { t, readOnly, patternsStore, loadPatternsOnOpen } = this.props;
    const { pattern, machines } = this.props.value;
    const { hasPattern, patternRows, loading } = this.state;
    //const requiredFieldsClassName = showRequired ? 'field required' : 'field';
    const { isBusy } = patternsStore.state;
    //const showWarningMessage = warningMessageEnabled && warningMessageEnabled === true && this.matchMachineClusteAndEquipment();

    return (
      <div className="w-full">
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">{t('Pattern')}</label>
          <div className="flex gap-2 mb-2">
            <button
              type="button"
              className={`px-4 py-1 rounded border text-sm font-semibold ${
                hasPattern ? 'bg-primary-600 text-white gray-border' : 'bg-white text-black-600 gray-border'
              } ${readOnly ? 'opacity-50 cursor-not-allowed' : ''}`}
              disabled={readOnly}
              onClick={() => this.handleOnHasPatternChange(true)}
            >
              {t('Yes')}
            </button>
            <button
              type="button"
              className={`px-4 py-1 rounded border text-sm font-semibold ${
                !hasPattern ? 'bg-primary-600 text-white gray-border' : 'bg-white text-black-600 gray-border'
              } ${readOnly ? 'opacity-50 cursor-not-allowed' : ''}`}
              disabled={readOnly}
              onClick={() => this.handleOnHasPatternChange(false)}
            >
              {t('No')}
            </button>
          </div>

          {hasPattern && (
            <div className="mt-2 w-full">
              <TailwindPatternEditor
                nullable
                clearable
                showOnlyActive
                readOnly={readOnly}
                className="w-full"
                value={pattern}
                onChange={this.handleOnPatternChange}
                loadDataOnOpen={loadPatternsOnOpen}
              />
            </div>
          )}
        </div>

        {machines && (
          <>
            <Dimmer style={{ zIndex: 999, background: 'rgba(0, 0, 0, 0.4)', position: 'fixed' }} active={loading}>
              <Loader indeterminate>{t('Please wait while respective machinery is being loaded ...')}</Loader>
            </Dimmer>
            {machines.map((machine, index) => {
              // Generate a stable key based on machine IDs to prevent remounting on deletes
              const machineKey =
                [machine.cluster?.id, machine.equipmentType?.id, machine.oem?.id, machine.machineModel?.id, index]
                  .filter(Boolean)
                  .join('-') || `empty-${index}`;

              return (
                <div key={machineKey} className="bg-gray-50 rounded shadow p-4 mb-4 flex flex-col gap-4">
                  <TailwindMachineListItem
                    pattern={hasPattern && (patternRows || []).length > index ? patternRows[index] : null}
                    machine={machine}
                    onEditMode={!readOnly && !isBusy}
                    canDelete={this.canDelete()}
                    onChange={value => this.handleOnMachineChange(index, value)}
                    onDeleteClose={() => this.handleOnDeleteRow(index)}
                    automaticallyAddedMachines={this.automaticallyAddedMachines()}
                  />
                </div>
              );
            })}
          </>
        )}

        {/*
        {showWarningMessage && (
          <div key={'warnmsgautomaticallyaddedequipment-mobile'} className="bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded mb-4">
            <div className="flex items-center gap-2">
              <span className="text-yellow-600 text-lg">⚠️</span>
              <p className="text-sm text-yellow-800">
                {t(
                  'Please add all respective machinery information if necessary or remove the unrelated ones by clicking the cross icon on the right side of each row.'
                )}
              </p>
            </div>
          </div>
        )} */}
      </div>
    );
  }
}

export default withTranslation()(TailwindPatternMachines);
